import React, { useEffect, useState } from "react";
import { Box } from "@mantine/core";
import { motion } from "framer-motion";
import { ThemeType } from "../../shared/util/types";
import { AllGuessesInType, CaptionType } from "./types";
import DynamicText from "../../shared/components/DynamicText";

type CaptionPresentationProps = {
  theme: ThemeType;
  results?: AllGuessesInType;
  onNewState: (state: string) => void;
  setReminder: (reminder: string) => void;
};

const CaptionPresentation = ({
  theme,
  results,
  onNewState,
  setReminder,
}: CaptionPresentationProps) => {
  const timeBetweenCaptionsSec = 10.0;
  const [presentingCaption, setPresentingCaption] = useState<
    CaptionType | undefined
  >(undefined);
  const [currentCaptionKey, setCurrentCaptionKey] = useState<string>("init");
  const getReminder = () => {
    const numPlayersPickedThis =
      presentingCaption?.PlayersThatGuessedThis?.length ?? 0;
    const player = presentingCaption?.PlayerName ?? "";
    const isTruth = player === results?.Prompt?.Truth.PlayerName;
    const pointsPerGuess = results?.PointsPerGuess ?? 0;
    return isTruth
      ? numPlayersPickedThis > 0
        ? pointsPerGuess +
          " Points for Picking the Truth, " +
          pointsPerGuess * numPlayersPickedThis +
          " Points to " +
          player
        : "Nobody Picked the Correct Prompt!"
      : numPlayersPickedThis > 0
      ? pointsPerGuess * numPlayersPickedThis +
        " Points to " +
        player +
        " for fooling " +
        numPlayersPickedThis +
        (numPlayersPickedThis > 1 ? " people!" : " person!")
      : "Nobody picked " + player + "'s Description :/";
  };
  const reminder = getReminder();
  useEffect(() => {
    setReminder(reminder);
  }, [reminder, setReminder]);
  useEffect(() => {
    const newResultsRoutine = async (results: AllGuessesInType) => {
      for (let i = 0; i < results?.Prompt?.Captions?.length; i++) {
        if (results?.Prompt?.Captions[i]?.PlayersThatGuessedThis?.length > 0) {
          setPresentingCaption(results.Prompt.Captions[i]);
          setCurrentCaptionKey(results.Prompt.Captions[i] + i.toString());
          await new Promise((resolve) =>
            setTimeout(resolve, timeBetweenCaptionsSec * 1000)
          );
        }
      }
      setPresentingCaption({
        PlayerName: results?.Prompt?.Truth.PlayerName,
        PlayersThatGuessedThis: results?.Prompt?.Truth.PlayersThatGuessedThis,
        Text: results?.Prompt?.Truth.Text,
      });
      setCurrentCaptionKey("truth");
      await new Promise((resolve) =>
        setTimeout(resolve, timeBetweenCaptionsSec * 1000)
      );
      onNewState("ShowScores");
    };
    results && newResultsRoutine(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);
  const playerTile = (player: string, index: number) => {
    return (
      <motion.div
        key={"caption-guess" + index + currentCaptionKey}
        animate={{ x: ["calc(110vh)", 0, 0, "calc(110vh)"] }}
        transition={{
          times: [0, 0.2, 0.9, 1],
          delay: 0.5 * (index + 1),
          duration: timeBetweenCaptionsSec - 1 - index * 0.5,
          ease: ["easeInOut"],
        }}
      >
        <DynamicText
          style={{
            border: "2px solid white",
            borderRadius: 20,
            alignContent: "center",
            textAlign: "center",
            justifyContent: "center",
            justifyItems: "center",
            padding: 5,
            margin: 15,
            fontSize: 20,
            width: 200,
          }}
        >
          {player}
        </DynamicText>
      </motion.div>
    );
  };
  const tileColumn = (players: string[], colNum: number, totalCols: number) => {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
        }}
      >
        {players.map((p, i) => {
          return playerTile(p, i + totalCols + colNum);
        })}
      </Box>
    );
  };
  const guessTiles = (players: string[]) => {
    return players.length > 3 ? (
      <Box
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
        }}
      >
        {tileColumn(players.slice(0, Math.floor(players.length / 2)), 0, 2)}
        {tileColumn(players.slice(Math.floor(players.length / 2)), 1, 2)}
      </Box>
    ) : (
      tileColumn(players, 0, 1)
    );
  };
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100% ",
        height: "100%",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <motion.div
        key={"caption" + currentCaptionKey}
        animate={{ x: ["calc(150vw)", 0, 0, "calc(150vw)"] }}
        transition={{
          times: [0, 0.2, 0.9, 1],
          duration: timeBetweenCaptionsSec,
          ease: "easeInOut",
        }}
      >
        <DynamicText
          style={{
            color: theme.TextColor,
            fontSize: 25,
            maxHeight: "calc(50vh)",
            justifyContent: "center",
          }}
        >
          {(presentingCaption && presentingCaption.Text) || ""}
        </DynamicText>
        {presentingCaption &&
          guessTiles(presentingCaption.PlayersThatGuessedThis)}
      </motion.div>
    </Box>
  );
};

export default CaptionPresentation;
